/**
 * Tela inicial.
 *
 * Nao importa um unico plugin. O que aparece aqui sao os widgets que os plugins
 * publicaram nos seus manifestos, ja ordenados e filtrados por permissoes pelo
 * registro. A medicacao de hoje e o gasto do mes aparecem porque os plugins de
 * saude e de financas os publicam — nao porque este arquivo os conheca (regra 1).
 *
 * Regra dos 2 toques: daqui, o assistente e cada area sao um unico toque.
 */
import { useNavigate } from 'react-router-dom';
import { Banner, BigButton, Card, EmptyState, List, ListItem, SectionHeading, Screen, Stack } from '@flos/ui';
import { useApp } from '../state/AppContext';
import { primaryRoutes, SHELL_PATHS } from '../navigation';
import { WidgetSlot } from '../components/WidgetSlot';

export function HomeScreen() {
  const { boot, manifests } = useApp();
  const navigate = useNavigate();

  const widgets = boot.core.registry.widgets();
  const areas = primaryRoutes(boot.core.registry.routes());
  const session = boot.core.session.peek();
  const broken = boot.failedPlugins;

  const nameOf = (pluginId: string): string =>
    manifests.find((manifest) => manifest.id === pluginId)?.name ?? pluginId;
  const iconOf = (pluginId: string): string =>
    manifests.find((manifest) => manifest.id === pluginId)?.icon ?? '📁';

  return (
    <Screen title={session ? `Olá, ${session.displayName}` : 'Início'}>
      <Stack gap="lg">
        {/* Voz é a entrada principal (ADR-0007): é o primeiro e o maior. */}
        <BigButton
          size="xl"
          icon="🎤"
          fullWidth
          onClick={() => navigate(SHELL_PATHS.ASSISTANT)}
        >
          Falar com o assistente
        </BigButton>

        {broken.length > 0 ? (
          <Banner tone="warning" title="Uma parte do aplicativo não abriu">
            {broken.map((plugin) => plugin.name).join(', ')}. O resto funciona
            normalmente. Pode ver os detalhes em Configurações.
          </Banner>
        ) : null}

        {widgets.length > 0 ? (
          <Stack gap="md">
            <SectionHeading>Hoje</SectionHeading>
            {widgets.map((widget) => (
              <WidgetSlot
                key={`${widget.pluginId}:${widget.id}`}
                widget={widget}
                pluginName={nameOf(widget.pluginId)}
                onError={(error) =>
                  boot.core.logger.error('Widget rebentou na tela inicial', {
                    plugin: widget.pluginId,
                    widget: widget.id,
                    erro: error.message,
                  })
                }
              />
            ))}
          </Stack>
        ) : null}

        {areas.length > 0 ? (
          <Stack gap="md">
            <SectionHeading>Áreas</SectionHeading>
            <List label="Áreas do aplicativo">
              {areas.map((route) => (
                <ListItem
                  key={route.fullPath}
                  title={route.title}
                  icon={iconOf(route.pluginId)}
                  onClick={() => navigate(route.fullPath)}
                />
              ))}
            </List>
          </Stack>
        ) : (
          <Card>
            <EmptyState
              icon="🌱"
              title="Ainda não há nada por aqui"
              description="Assim que ligar as áreas que quer usar, elas aparecem nesta tela."
              action={
                <BigButton icon="⚙️" onClick={() => navigate(SHELL_PATHS.SETTINGS)}>
                  Abrir Configurações
                </BigButton>
              }
            />
          </Card>
        )}
      </Stack>
    </Screen>
  );
}

export default HomeScreen;
